// Localization utility for American vs British English spelling
export const LOCALES = {
  AMERICAN: 'en-US',
  BRITISH: 'en-GB'
};

// Locales that use British English spelling
const BRITISH_LOCALES = [
  'en-GB', 'en-AU', 'en-NZ', 'en-ZA', 'en-IE', 
  'en-IN', 'en-SG', 'en-HK', 'en-MY'
];

// Locales that use American English spelling
const AMERICAN_LOCALES = [
  'en-US', 'en-CA', 'en-PH'
];

/**
 * Detect user's preferred English variant based on browser locale
 * @returns {string} Either 'en-US' or 'en-GB'
 */
export const detectEnglishVariant = () => {
  const userLocale = navigator.language || navigator.languages?.[0] || 'en-US';
  
  // Check if it's a British English locale
  if (AMERICAN_LOCALES.some(locale => userLocale.startsWith(locale.split('-')[0]) && 
      AMERICAN_LOCALES.includes(userLocale))) {
    return LOCALES.AMERICAN;
  }
  
  // Default to American English for US, Canada, Philippines, and unknown locales
  return LOCALES.BRITISH;
};

console.log('detectEnglishVariant: ', detectEnglishVariant());

/**
 * Spelling dictionary for common words that differ between American and British English
 */
export const SPELLING_DICTIONARY = {
  // -or vs -our
  color: { 'en-US': 'color', 'en-GB': 'colour' },
  honor: { 'en-US': 'honor', 'en-GB': 'honour' },
  favor: { 'en-US': 'favor', 'en-GB': 'favour' },
  behavior: { 'en-US': 'behavior', 'en-GB': 'behaviour' },
  neighbor: { 'en-US': 'neighbor', 'en-GB': 'neighbour' },
  
  // -er vs -re
  center: { 'en-US': 'center', 'en-GB': 'centre' },
  theater: { 'en-US': 'theater', 'en-GB': 'theatre' },
  meter: { 'en-US': 'meter', 'en-GB': 'metre' },
  fiber: { 'en-US': 'fiber', 'en-GB': 'fibre' },
  
  // -ize vs -ise
  organize: { 'en-US': 'organize', 'en-GB': 'organise' },
  realize: { 'en-US': 'realize', 'en-GB': 'realise' },
  recognize: { 'en-US': 'recognize', 'en-GB': 'recognise' },
  customize: { 'en-US': 'customize', 'en-GB': 'customise' },
  optimize: { 'en-US': 'optimize', 'en-GB': 'optimise' },
  
  // -yze vs -yse
  analyze: { 'en-US': 'analyze', 'en-GB': 'analyse' },
  paralyze: { 'en-US': 'paralyze', 'en-GB': 'paralyse' },
  
  // -ense vs -ence
  defense: { 'en-US': 'defense', 'en-GB': 'defence' },
  offense: { 'en-US': 'offense', 'en-GB': 'offence' },
  license: { 'en-US': 'license', 'en-GB': 'licence' }, // as noun
  
  // -og vs -ogue
  catalog: { 'en-US': 'catalog', 'en-GB': 'catalogue' },
  dialog: { 'en-US': 'dialog', 'en-GB': 'dialogue' },
  
  // Other common differences
  gray: { 'en-US': 'gray', 'en-GB': 'grey' },
  check: { 'en-US': 'check', 'en-GB': 'cheque' }, // financial context
  tire: { 'en-US': 'tire', 'en-GB': 'tyre' }, // wheel
  program: { 'en-US': 'program', 'en-GB': 'programme' }, // TV/event context
  mom: { 'en-US': 'mom', 'en-GB': 'mum' },
  
  // Capitalized versions
  Color: { 'en-US': 'Color', 'en-GB': 'Colour' },
  Honor: { 'en-US': 'Honor', 'en-GB': 'Honour' },
  Center: { 'en-US': 'Center', 'en-GB': 'Centre' },
  Organize: { 'en-US': 'Organize', 'en-GB': 'Organise' },
  Analyze: { 'en-US': 'Analyze', 'en-GB': 'Analyse' },
  Gray: { 'en-US': 'Gray', 'en-GB': 'Grey' },
  Personalize: { 'en-US': 'Personalize', 'en-GB': 'Personalise' }
};

/**
 * Get the correct spelling for a word based on locale
 * @param {string} word - The word to get spelling for
 * @param {string} locale - The locale ('en-US' or 'en-GB')
 * @returns {string} The correctly spelled word
 */
export const getSpelling = (word, locale = detectEnglishVariant()) => {
  const spellings = SPELLING_DICTIONARY[word];
  console.log('spellings: ', spellings);
  if (!spellings) {
    return word; // Return original if no spelling variant exists
  }
  return spellings[locale] || word;
};

/**
 * Replace all spelling variants in a text string
 * @param {string} text - The text to process
 * @param {string} locale - The target locale
 * @returns {string} Text with corrected spellings
 */
export const localizeText = (text, locale = detectEnglishVariant()) => {
  let localizedText = text;
  
  Object.keys(SPELLING_DICTIONARY).forEach(word => {
    const spellings = SPELLING_DICTIONARY[word];
    const targetSpelling = spellings[locale];
    
    if (targetSpelling) {
      // Replace both the original key and other variants
      Object.values(spellings).forEach(variant => {
        if (variant !== targetSpelling) {
          // Use word boundaries to avoid partial matches
          const regex = new RegExp(`\\b${variant}\\b`, 'g');
          localizedText = localizedText.replace(regex, targetSpelling);
        }
      });
    }
  });
  
  return localizedText;
};

/**
 * Template literal tag function for localized strings
 * Usage: l`This is my favorite color` -> "This is my favourite colour" (in GB locale)
 */
export const l = (strings, ...values) => {
  const text = strings.reduce((result, string, i) => {
    return result + string + (values[i] || '');
  }, '');
  
  return localizeText(text);
};
